import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AbandonDossierGQL,
  AbandonDossierMutationVariables,
  BulkCreateEventGQL,
  BulkCreateEventMutationVariables,
  CreateEventGQL,
  CreateEventMutationVariables,
  DeleteEventGQL,
  DeleteEventMutationVariables,
  EventGQL,
  EventQueryVariables,
  EventsGQL,
  EventsQueryVariables,
  GetAllFittersByIdGQL,
  GetAllFittersByIdQueryVariables,
  SaveDraftDossierGQL,
  SaveDraftDossierMutationVariables,
  SearchDossierByCustomerNameGQL,
  SearchDossierByCustomerNameQueryVariables,
  SearchDossiersAgendaGQL,
  SearchDossiersAgendaOverlapGQL,
  SearchDossiersAgendaOverlapQueryVariables,
  SearchDossiersAgendaQueryVariables,
  SearchDossiersRequestedApmtAgendaGQL,
  SearchDossiersRequestedApmtAgendaQueryVariables,
  SearchDossiersToValidateAgendaGQL,
  SearchDossiersToValidateAgendaQueryVariables,
  UpdateEventGQL,
  UpdateEventMutationVariables,
} from '@sc/data-access-graphql';
import {
  AgendaDashboardAPIParams,
  AgendaEvent,
  CancelAppointmentApi,
  Dossier,
  Fitter,
  GetFittersSuggestionsApi,
  GetFittersSuggestionsApiResponse,
  NotifyAppointmentApi,
} from '@sc/data-access-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AgendaHttpService {
  private readonly bookAppointmentAgendaUrl = '/api/agenda/appointment/book';
  private readonly modifyAppointmentAgendaUrl =
    '/api/agenda/appointment/modify';
  private readonly cancelAppointmentAgendaUrl =
    '/api/agenda/appointment/cancel';
  private readonly notifyAppointmentAgendaUrl =
    '/api/agenda/appointment/notify';
  private readonly countersDashboardAgendaUrl = '/api/agenda/dashboard/stats';
  private readonly exportDashboardAgendaUrl = '/api/agenda/dashboard/export';
  private readonly fittersSuggestionsAgendaUrl =
    '/api/agenda/fitters/suggestion';
  private readonly agendaSaveLightDossier = '/api/agenda/dossier/save';
  private readonly exportTimeout = '120000';
  private readonly agendaGeneralTimeout = '30000';

  constructor(
    private readonly httpClient: HttpClient,
    private readonly searchDossierAgendaGQL: SearchDossiersAgendaGQL,
    private readonly searchDossierOverlapAgendaGQL: SearchDossiersAgendaOverlapGQL,
    private readonly searchDossierToValidateAgendaGQL: SearchDossiersToValidateAgendaGQL,
    private readonly searchDossierRequestedApmtAgendaGQL: SearchDossiersRequestedApmtAgendaGQL,
    private readonly getAllFittersByIdGQL: GetAllFittersByIdGQL,
    private readonly eventsGQL: EventsGQL,
    private readonly eventGQL: EventGQL,
    private readonly createEventGQL: CreateEventGQL,
    private readonly bulkCreateEventGQL: BulkCreateEventGQL,
    private readonly updateEventGQL: UpdateEventGQL,
    private readonly deleteEventGQL: DeleteEventGQL,
    private readonly abandonDossierGQL: AbandonDossierGQL,
    private readonly saveDraftGQL: SaveDraftDossierGQL,
    private readonly searchDossierByCustomerName: SearchDossierByCustomerNameGQL
  ) {}

  getDossiers(
    params: SearchDossiersAgendaQueryVariables
  ): Observable<Partial<Dossier>[]> {
    return this.searchDossierAgendaGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.searchDossiersAgenda) {
            return response.data
              .searchDossiersAgenda as unknown as Partial<Dossier>[];
          }
          return [];
        })
      );
  }

  getDossiersOverlap(params: SearchDossiersAgendaOverlapQueryVariables) {
    return this.searchDossierOverlapAgendaGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response?.data?.searchDossiersAgendaOverlap) {
            return response.data
              .searchDossiersAgendaOverlap as unknown as Dossier[];
          }
          return [];
        })
      );
  }

  getDossiersToValidate(params: SearchDossiersToValidateAgendaQueryVariables) {
    return this.searchDossierToValidateAgendaGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response?.data?.searchDossiersToValidateAgenda) {
            return response.data
              .searchDossiersToValidateAgenda as unknown as Dossier[];
          }
          return [];
        })
      );
  }

  getRequestApmtDossiers(
    params: SearchDossiersRequestedApmtAgendaQueryVariables
  ) {
    return this.searchDossierRequestedApmtAgendaGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response?.data?.searchDossiersRequestedApmtAgenda) {
            return response.data
              .searchDossiersRequestedApmtAgenda as unknown as Partial<Dossier>[];
          }
          return [];
        })
      );
  }

  searchDossiersByCustomerName(
    params: SearchDossierByCustomerNameQueryVariables
  ) {
    return this.searchDossierByCustomerName
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          return response.data.searchDossierByCustomerName;
        })
      );
  }

  getFittersByIds(params: GetAllFittersByIdQueryVariables) {
    return this.getAllFittersByIdGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          return response.data.getAllFittersById as Fitter[];
        })
      );
  }

  bookAppointment(params: any) {
    return this.httpClient.post<{ dossier: Dossier }>(
      this.bookAppointmentAgendaUrl,
      params,
      {
        headers: {
          requestName: 'appointments-http bookAppointmentAgenda',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }

  saveLightDossier(params: any) {
    return this.httpClient.post<{ dossier: Dossier }>(
      this.agendaSaveLightDossier,
      params,
      {
        headers: {
          requestName: 'appointments-http saveLightDossier',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }

  modifyAppointment(params: { dossier: Dossier }) {
    return this.httpClient.post<{ dossier: Dossier }>(
      this.modifyAppointmentAgendaUrl,
      params,
      {
        headers: {
          requestName: 'appointments-http modifyAppointmentAgenda',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }
  cancelAppointment(params: CancelAppointmentApi) {
    return this.httpClient.post<{ dossier: Dossier }>(
      this.cancelAppointmentAgendaUrl,
      params,
      {
        headers: {
          requestName: 'appointments-http cancelAppointmentAgenda',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }

  notifyAppointment(params: NotifyAppointmentApi) {
    return this.httpClient.post<{ state: string }>(
      this.notifyAppointmentAgendaUrl,
      params,
      {
        headers: {
          requestName: 'appointments-http notifyAppointmentAgenda',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }

  getEvents(params: EventsQueryVariables): Observable<AgendaEvent[]> {
    return this.eventsGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.events) {
            return response.data.events as AgendaEvent[];
          }
          return [];
        })
      );
  }

  getEvent(params: EventQueryVariables): Observable<AgendaEvent> {
    return this.eventGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response) => {
          if (response.data.event) return response.data.event as AgendaEvent;
          else throw new Error('AgendaEvent not found');
        })
      );
  }

  createEvent(params: CreateEventMutationVariables): Observable<AgendaEvent> {
    return this.createEventGQL.mutate(params).pipe(
      map((response) => {
        return response.data.createEvent as AgendaEvent;
      })
    );
  }

  bulkCreateEvent(
    params: BulkCreateEventMutationVariables
  ): Observable<AgendaEvent[]> {
    return this.bulkCreateEventGQL.mutate(params).pipe(
      map((response) => {
        return response.data.bulkCreateEvent as AgendaEvent[];
      })
    );
  }

  updateEvent(params: UpdateEventMutationVariables): Observable<AgendaEvent> {
    return this.updateEventGQL.mutate(params).pipe(
      map((response) => {
        return response.data.updateEvent as AgendaEvent;
      })
    );
  }

  deleteEvent(params: DeleteEventMutationVariables): Observable<boolean> {
    return this.deleteEventGQL.mutate(params).pipe(
      map((response) => {
        return response.data.deleteEvent;
      })
    );
  }

  fetchStats(params: AgendaDashboardAPIParams) {
    return this.httpClient.post<any>(this.countersDashboardAgendaUrl, params, {
      headers: {
        requestName: 'agenda-dashboard-http fetchStats',
        timeout: this.agendaGeneralTimeout,
      },
    });
  }

  exportData(params: AgendaDashboardAPIParams) {
    return this.httpClient.post<any>(this.exportDashboardAgendaUrl, params, {
      headers: {
        requestName: 'agenda-dashboard-http exportData',
        timeout: this.exportTimeout,
      },
    });
  }

  getFitterSuggestions(params: GetFittersSuggestionsApi) {
    return this.httpClient.post<GetFittersSuggestionsApiResponse>(
      this.fittersSuggestionsAgendaUrl,
      params,
      {
        headers: {
          requestName: 'agenda-http getFitterSuggestions',
          timeout: this.agendaGeneralTimeout,
        },
      }
    );
  }

  saveDraftDossier(params: SaveDraftDossierMutationVariables) {
    return this.saveDraftGQL.mutate(params).pipe(
      map((response) => {
        return response.data.saveDraftDossier;
      })
    );
  }

  abandonDossier(params: AbandonDossierMutationVariables): Observable<boolean> {
    return this.abandonDossierGQL.mutate(params).pipe(
      map((response) => {
        return response.data.abandonDossier?.abandonned;
      })
    );
  }
}
